import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Alert, Button, Form, message, Modal, Tooltip, Upload,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, InboxOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import {
  csvImportOrders, csvPreviewOrders, csvReanalyzeOrders,
} from '../../../store/actions/orders/importOrderCsv.actions';
import { getPayloadData, isSuccess } from '../../../utils/responseUtils';
import ImportOrderPreviewStep from './ImportOrderPreviewStep';
import useImportOrderPreview, { normalizeOrdersForImport } from './useImportOrderPreview';

const Guidelines = () => {
  const { t } = useTranslation();
  const requiredFields = [
    'orderNumber',
    'startDate',
    'endDate',
    'loadLatitude',
    'loadLongitude',
    'unloadLatitude',
    'unloadLongitude',
  ];
  const exampleDate = moment().format('YYYY-MM-DDTHH:mm:ssZ');

  const requiredFieldsTooltip = `${t('ORDER.IMPORT_ORDER.GUIDELINES.REQUIRED_COLUMNS')} ${requiredFields.join(', ')}`;

  return (
    <ul>
      <li>
        {t('ORDER.IMPORT_ORDER.GUIDELINES.REQUIRED_COLUMNS_INFO')}
        {' '}
        <Tooltip title={requiredFieldsTooltip}>
          <InfoCircleOutlined />
        </Tooltip>
      </li>
      <li>
        {t('ORDER.IMPORT_ORDER.GUIDELINES.DATETIME_FORMAT_INFO')}
        {' '}
        <i>{t('ORDER.IMPORT_ORDER.GUIDELINES.DATETIME_FORMAT_STRING')}</i>
        {' '}
        <Tooltip
          title={`${t('ORDER.IMPORT_ORDER.GUIDELINES.DATETIME_EXAMPLE_PREFIX')} ${exampleDate}`}
          overlayStyle={{ whiteSpace: 'nowrap' }}
        >
          <InfoCircleOutlined />
        </Tooltip>
      </li>
      <li>{t('ORDER.IMPORT_ORDER.GUIDELINES.SEPARATOR_INFO')}</li>
      <li>{t('ORDER.IMPORT_ORDER.GUIDELINES.QUOTES_INFO')}</li>
      <li>{t('ORDER.IMPORT_ORDER.GUIDELINES.MAX_ROWS_INFO')}</li>
    </ul>
  );
};

const ImportOrderModal = ({
  visible, onCancel, onSuccess, contextCompanyId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [analysisStatus, setAnalysisStatus] = useState('idle'); // idle | error
  const previewState = useImportOrderPreview({ visible, step });
  const { clearPreviewState } = previewState;

  useEffect(() => {
    if (!visible) {
      setFileList([]);
      setStep(1);
      setAnalysisStatus('idle');
      clearPreviewState();
      form.resetFields();
    }
  }, [visible, form, clearPreviewState]);

  const reanalyzeCurrentPreview = ({ showFailureMessage = true } = {}) => {
    const orders = normalizeOrdersForImport(previewState.getAllOrdersForValidation());
    if (!orders.length) {
      setLoading(false);
      return Promise.resolve(null);
    }

    setLoading(true);
    return dispatch(csvReanalyzeOrders(orders, contextCompanyId))
      .then((response) => {
        if (isSuccess(response)) {
          const data = getPayloadData(response) || {};
          previewState.applyPreviewData(data);
        } else if (showFailureMessage) {
          message.error(t('ORDER.IMPORT_ORDER.REANALYZE_FAILED'));
        }
        setLoading(false);
        return response;
      });
  };

  const handleOk = () => {
    if (step === 1) {
      if (fileList.length === 0) {
        form.validateFields();
        return;
      }

      setLoading(true);
      setAnalysisStatus('idle');
      dispatch(csvPreviewOrders(fileList[0], contextCompanyId))
        .then((response) => {
          if (isSuccess(response)) {
            const data = getPayloadData(response) || {};
            previewState.applyPreviewData(data);
            setStep(2);
          } else {
            setAnalysisStatus('error');
          }
          setLoading(false);
        });
      return;
    }

    if (previewState.fileLevelErrors.length) {
      message.warning(t('ORDER.IMPORT_ORDER.FILE_ERRORS_DESC'));
      return;
    }

    if (previewState.hasUnvalidatedChanges) {
      message.warning(t('ORDER.IMPORT_ORDER.REVALIDATION_REQUIRED_AFTER_EDITS'));
      return;
    }

    const ordersToImport = previewState.getOrdersToImport();
    if (!ordersToImport.length) {
      message.warning(t('COMMON.PLEASE_SELECT_AT_LEAST_ONE'));
      return;
    }

    setLoading(true);
    dispatch(csvImportOrders(normalizeOrdersForImport(ordersToImport), contextCompanyId))
      .then((response) => {
        if (isSuccess(response)) {
          message.success(t('ORDER.IMPORT_ORDER.SUCCESS'));
          onSuccess();
          onCancel();
          setLoading(false);
          return;
        }
        reanalyzeCurrentPreview({ showFailureMessage: false });
      });
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isCsv = file.type === 'text/csv' || file.type === 'application/vnd.ms-excel' || file.name.endsWith('.csv');
      if (!isCsv) {
        message.error(t('ORDER.IMPORT_ORDER.ONLY_CSV'));
        return Upload.LIST_IGNORE;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error(t('ORDER.IMPORT_ORDER.FILE_TOO_LARGE'));
        return Upload.LIST_IGNORE;
      }
      const newFile = new File([file], file.name, { type: 'text/csv' });
      setFileList([newFile]);
      form.setFieldsValue({ file: [newFile] });
      return Upload.LIST_IGNORE;
    },
  };

  const importDisabled = useMemo(() => (
    previewState.fileLevelErrors.length > 0
      || previewState.selectedRowKeys.length === 0
      || previewState.hasUnvalidatedChanges
      || previewState.selectedRowKeys.some((k) => previewState.errorsByIndex.has(k))
  ), [
    previewState.fileLevelErrors,
    previewState.selectedRowKeys,
    previewState.hasUnvalidatedChanges,
    previewState.errorsByIndex,
  ]);

  return (
    <Modal
      className={`ai-import-order-modal${step === 2 ? ' aiio-preview-modal' : ''}`}
      destroyOnClose
      open={visible}
      title={t('ORDER.IMPORT_FILE')}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      okText={step === 1 ? t('COMMON.NEXT') : t('ORDER.IMPORT_ORDER.IMPORT_SELECTED')}
      width={step === 2 ? '96vw' : 700}
      style={step === 2 ? { maxWidth: 1560 } : undefined}
      footer={step === 2 ? (
        <div className="aiio-footer">
          <div>
            <Button onClick={() => setStep(1)}>
              {t('COMMON.BACK')}
            </Button>
          </div>
          <div className="aiio-flex-row aiio-align-center aiio-gap-8">
            <Button onClick={onCancel} className="aiio-mr-8">
              {t('COMMON.CANCEL')}
            </Button>
            <Button onClick={() => reanalyzeCurrentPreview()} loading={loading}>
              {t('ORDER.IMPORT_ORDER.REVIEW_AGAIN')}
            </Button>
            <Button
              type="primary"
              onClick={handleOk}
              loading={loading}
              disabled={importDisabled}
            >
              {t('ORDER.IMPORT_ORDER.IMPORT_SELECTED')}
            </Button>
          </div>
        </div>
      ) : undefined}
    >
      {step === 1 && (
        <Form
          form={form}
          layout="vertical"
          name="importOrder"
        >
          {analysisStatus === 'error' && (
            <Alert
              message={<b>{t('COMMON.ERROR')}</b>}
              description={t('ORDER.IMPORT_ORDER.CSV_ANALYSIS_ERROR_DESC')}
              type="error"
              showIcon
              className="aiio-mb-16"
            />
          )}
          <Form.Item
            name="file"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={[{ required: true, message: t('ORDER.IMPORT_ORDER.SELECT_FILE') }]}
          >
            <Upload.Dragger
              name={uploadProps.name}
              multiple={uploadProps.multiple}
              fileList={uploadProps.fileList}
              onRemove={uploadProps.onRemove}
              beforeUpload={uploadProps.beforeUpload}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">{t('ORDER.IMPORT_ORDER.DRAG_AND_DROP')}</p>
              <p className="ant-upload-hint">{t('ORDER.IMPORT_ORDER.SUPPORT_TEXT')}</p>
            </Upload.Dragger>
          </Form.Item>
          <Alert
            message={<b>{t('ORDER.IMPORT_ORDER.GUIDELINES.TITLE')}</b>}
            description={<Guidelines />}
            type="info"
            showIcon
            style={{ marginBottom: '24px' }}
          />
          <a href="/mo_import_example.csv" download>
            <DownloadOutlined style={{ marginRight: '8px' }} />
            {t('ORDER.IMPORT_ORDER.DOWNLOAD_EXAMPLE')}
          </a>
        </Form>
      )}
      {step === 2 && (
        <ImportOrderPreviewStep previewState={previewState} />
      )}
    </Modal>
  );
};

ImportOrderModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  contextCompanyId: PropTypes.string.isRequired,
};

export default ImportOrderModal;
